import { supabase, withAuthRetry } from './supabase';
import { logger } from './logger';

/** Compte support technique (migration 079, public.powerjobs_user_id()). */
export const POWERJOBS_USER_ID = '00000000-0000-4000-8000-000000000001';

export interface SupportUserCard {
  userId: string;
  role: string;
  displayName: string;
  photoUrl: string | null;
  subtitle: string | null;
}

export interface SupportConversation {
  id: string;
  other: SupportUserCard;
  otherEmail: string | null;
  otherRole: 'candidate' | 'employer' | 'visitor' | 'admin' | string;
  otherProfileId: string | null;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  lastSenderId: string | null;
  unreadCount: number;
  createdAt: string;
}

export interface SupportMessage {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  createdAt: string;
}

function mapCard(raw: any): SupportUserCard {
  return {
    userId: raw?.user_id || '',
    role: raw?.role || 'candidate',
    displayName: (raw?.display_name || '').trim() || 'Utilisateur',
    photoUrl: raw?.photo_url || null,
    subtitle: raw?.subtitle || null,
  };
}

function mapConversation(raw: any): SupportConversation {
  const other = mapCard(raw.other);
  // Profil sans nom renseigné (onboarding inachevé) : l'email reste identifiant.
  if (!(raw?.other?.display_name || '').trim() && raw.other_email) {
    other.displayName = raw.other_email;
  }
  return {
    id: raw.id,
    other,
    otherEmail: raw.other_email || null,
    otherRole: raw.other_role || 'candidate',
    otherProfileId: raw.other_profile_id || null,
    lastMessagePreview: raw.last_message_preview || null,
    lastMessageAt: raw.last_message_at || null,
    lastSenderId: raw.last_sender_id || null,
    unreadCount: Number(raw.unread_count) || 0,
    createdAt: raw.created_at,
  };
}

function mapMessage(row: any): SupportMessage {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    body: row.body,
    createdAt: row.created_at,
  };
}

export async function listSupportConversations(query = '', limit = 200): Promise<SupportConversation[]> {
  const { data, error } = await withAuthRetry(async () => await supabase
    .rpc('admin_support_list_conversations', {
      p_query: query.trim() || null,
      p_limit: limit,
      p_offset: 0,
    }));
  if (error) {
    logger.error('list_support_conversations', 'supportService', { error: error.message });
    throw error;
  }
  const rows = Array.isArray(data) ? data : [];
  return rows.map(mapConversation);
}

export async function fetchSupportMessages(conversationId: string, limit = 200): Promise<SupportMessage[]> {
  const { data, error } = await withAuthRetry(async () => await supabase
    .from('messages')
    .select('id, conversation_id, sender_id, body, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit));
  if (error) {
    logger.error('fetch_support_messages', 'supportService', { conversationId, error: error.message });
    throw error;
  }
  return (data || []).map(mapMessage).reverse();
}

export async function sendSupportMessage(userId: string, body: string): Promise<SupportMessage> {
  logger.functional('send_support_message', 'supportService', { userId });
  const { data, error } = await withAuthRetry(async () => await supabase
    .rpc('admin_support_send_message', { p_user_id: userId, p_body: body }));
  if (error) {
    logger.error('send_support_message', 'supportService', { userId, error: error.message });
    throw error;
  }
  const message = mapMessage(data);
  // Push best-effort : l'Edge Function accepte un admin sur une conversation PowerJobs.
  supabase.functions
    .invoke('notify-new-message', { body: { conversationId: message.conversationId, preview: body } })
    .catch(() => {});
  return message;
}

export async function markSupportRead(conversationId: string): Promise<void> {
  const { error } = await withAuthRetry(async () => await supabase
    .rpc('admin_support_mark_read', { p_conversation_id: conversationId }));
  if (error) {
    logger.error('mark_support_read', 'supportService', { conversationId, error: error.message });
  }
}

export async function fetchSupportUnreadTotal(): Promise<number> {
  const { data, error } = await withAuthRetry(async () => await supabase.rpc('admin_support_unread_total'));
  if (error) return 0;
  return Number(data) || 0;
}

/** Realtime : tout changement d'état de lecture côté PowerJobs (nouveau message reçu, lu…). */
export function subscribeSupportInbox(onChange: () => void): () => void {
  const channel = supabase
    .channel(`support-inbox:${Math.random().toString(36).slice(2)}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'conversation_user_state', filter: `user_id=eq.${POWERJOBS_USER_ID}` },
      onChange,
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

export function subscribeSupportThread(
  conversationId: string,
  onInsert: (message: SupportMessage) => void,
): () => void {
  const channel = supabase
    .channel(`support-conv:${conversationId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
      (payload) => {
        if (payload.new) onInsert(mapMessage(payload.new));
      },
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
